import Service from '@/services/balancer/contracts/balancer-contracts.service';
import ConfigService from '@/services/config/config.service';
import { call, Multicaller } from '@/lib/utils/balancer/contract';
import { default as XEmbrAbi } from '@/embr/abi/XEmbr.json';
import { default as VEWaveAbi } from '@/embr/abi/VEWaveAbi.json';
import { default as VEWaveEmissionDist_ABI } from '@/embr/abi/VEWaveEmissionDistAbi.json';
import { default as VEWaveRewardsMasterChef_ABI } from '@/embr/abi/VEWaveMasterchefPoolsAbi.json';
import { default as ERC20Abi } from '@/lib/abi/ERC20.json';
import { BigNumber } from 'ethers';
import { sendTransaction } from '@/lib/utils/balancer/web3';
import { Web3Provider } from '@ethersproject/providers';
import VEWavePoolsList from '@/components/tables/VeWaveTable/VEWavePools.json';
import { ref } from 'vue';
import { userInfo } from 'os';

interface GlobalData {
  periodFinish: BigNumber;
  lastUpdateTime: BigNumber;
  rewardRate: BigNumber;
  rewardPerTokenStored: BigNumber;
}

interface UserData {
  rewardPerTokenPaid: BigNumber;
  rewards: BigNumber;
  rewardsPaid: BigNumber;
}

interface Balance {
  /// units of staking token that has been deposited and consequently wrapped
  raw: BigNumber;
  /// (block.timestamp - weightedTimestamp) represents the seconds a user has had their full raw balance wrapped.
  /// If they deposit or withdraw, the weightedTimestamp is dragged towards block.timestamp proportionately
  weightedTimestamp: BigNumber;
  /// multiplier awarded for staking for a long time
  timeMultiplier: BigNumber;
  /// multiplier duplicated from QuestManager
  questMultiplier: BigNumber;
  /// Time at which the relative cooldown began
  cooldownTimestamp: BigNumber;
  /// Units up for cooldown
  cooldownUnits: BigNumber;
}

export default class VEWave {
  service: Service;

  constructor(service, private readonly configService = new ConfigService()) {
    this.service = service;
  }

  public async getData(
    account: string
  ): Promise<{
    embrBalance: BigNumber;
    userStaking: Balance;
    xEmbrBalance: BigNumber;
  }> {
    const multicaller = new Multicaller(this.configService.network.key, this.service.provider, XEmbrAbi);

    multicaller.call('embrBalance', this.embrAddress, 'balanceOf', [account]);
    multicaller.call('xEmbrBalance', this.xembrAddress, 'balanceOf', [account]);
    multicaller.call('userStaking', this.xembrAddress, 'balanceData', [account]);

    return multicaller.execute();
  }

  public async getTotalXEmbrSupply(): Promise<BigNumber> {
    return await call(this.service.provider, XEmbrAbi, [this.xembrAddress, 'totalSupply']);
  }

  public async allowance(account: string): Promise<BigNumber> {
    return await call(this.service.provider, ERC20Abi, [this.embrAddress, 'allowance', [account, this.xembrAddress]]);
  }

  public async xEmbrBalanceOf(account: string): Promise<BigNumber> {
    return await call(this.service.provider, XEmbrAbi, [this.xembrAddress, 'balanceOf', [account]]);
  }

  public async earned(account: string, pid: string): Promise<BigNumber> {
    return await call(this.service.provider, XEmbrAbi, [this.xembrAddress, 'earned', [BigNumber.from(pid), account]]);
  }

  public async userData(pid: string, account: string): Promise<UserData> {
    return await call(this.service.provider, XEmbrAbi, [this.xembrAddress, 'userData', [BigNumber.from(pid), account]]);
  }

  public async getGlobalData(tid: string): Promise<GlobalData> {
    return await call(this.service.provider, XEmbrAbi, [this.xembrAddress, 'globalData', [BigNumber.from(tid)]]);
  }

  public async getRewardToken(tid: string): Promise<string> {
    return await call(this.service.provider, XEmbrAbi, [this.xembrAddress, 'getRewardToken', [BigNumber.from(tid)]]);
  }

  public async activeTokenCount(): Promise<BigNumber> {
    return await call(this.service.provider, XEmbrAbi, [this.xembrAddress, 'activeTokenCount', []]);
  }

  public async reviewTimestamp(provider: Web3Provider, account: string) {
    return sendTransaction(provider, this.xembrAddress, XEmbrAbi, 'reviewTimestamp', [account]);
  }

  public async embrBalanceOf(account: string): Promise<BigNumber> {
    return await call(this.service.provider, ERC20Abi, [this.embrAddress, 'balanceOf', [account]]);
  }

  public async stake(provider: Web3Provider, amount: string) {
    return sendTransaction(provider, this.xembrAddress, XEmbrAbi, 'stake(uint256)', [BigNumber.from(amount)]);
  }

  public async stakeDelegate(provider: Web3Provider, amount: string, delegate: string) {
    return sendTransaction(provider, this.xembrAddress, XEmbrAbi, 'stake(uint256, address)', [
      BigNumber.from(amount),
      delegate
    ]);
  }

  public async stakeExitCooldown(provider: Web3Provider, amount: string, exitCooldown: boolean) {
    return sendTransaction(provider, this.xembrAddress, XEmbrAbi, 'stake(uint256, bool)', [
      BigNumber.from(amount),
      exitCooldown
    ]);
  }

  public async startCooldown(provider: Web3Provider, amount: string) {
    return sendTransaction(provider, this.xembrAddress, XEmbrAbi, 'startCooldown', [BigNumber.from(amount)]);
  }

  public async endCooldown(provider: Web3Provider) {
    return sendTransaction(provider, this.xembrAddress, XEmbrAbi, 'endCooldown', []);
  }

  public async withdraw(provider: Web3Provider, amount: string, receipent: string, exitCooldown: boolean) {
    return sendTransaction(provider, this.xembrAddress, XEmbrAbi, 'withdraw', [
      BigNumber.from(amount),
      receipent,
      true,
      exitCooldown
    ]);
  }

  public async claimAll(provider: Web3Provider) {
    return sendTransaction(provider, this.xembrAddress, XEmbrAbi, 'claimRewards', []);
  }

  public async claim(provider: Web3Provider, pid: string) {
    return sendTransaction(provider, this.xembrAddress, XEmbrAbi, 'claimReward(uint256)', [BigNumber.from(pid)]);
  }

  public async claimRedemption(provider: Web3Provider) {
    return sendTransaction(provider, this.xembrAddress, XEmbrAbi, 'claimRedemptionReward', []);
  }

  public get embrAddress(): string {
    return this.service.config.addresses.embr || '';
  }
  public get xembrAddress(): string {
    return this.service.config.xEmbr.address || '';
  }

  /* NEW FUNCTIONS */
  public get veWaveAddress(): string {
    return this.service.config.veWave?.address || '';
  }

  public get veWaveReceiptAddress(): string {
    return this.service.config.veWave?.receipt_address || '';
  }

  public get veWaveEmissionDistAddress(): string {
    return this.service.config.veWave?.emission_dist_address || '';
  }

  public get veWaveRewardsMasterchefAddress(): string {
    return this.service.config.veWave?.rewards_masterchef || '';
  }

  public async createLockVEWave(provider: Web3Provider, amount: string, lockTime: string) {
    return sendTransaction(
      provider,
      '0xfF76b49194B2a63A1746E045f44319F256C21d26',
      VEWaveAbi,
      'create_lock(uint256,uint256)',
      [BigNumber.from(amount), Number(lockTime)]
    );
  }

  public async leaveVEWaveByTokenId(provider: Web3Provider, tokenId: string) {
    return sendTransaction(provider, this.veWaveAddress, VEWaveAbi, 'leave(uint256)', [Number(tokenId)]);
  }

  public async stakeVEWaveByTokenId(provider: Web3Provider, pid: string, tokenId: string) {
    return sendTransaction(
      provider,
      '0xb60eA57c3108d40F81E621B4fc05E0587D5f9eC7',
      VEWaveEmissionDist_ABI,
      'depositToChef', // PID, TOKENID
      [Number(pid), Number(tokenId)]
    );
  }

  public async harvestReceipt(provider: Web3Provider, pid: string, account: string) {
    return sendTransaction(
      provider,
      this.veWaveRewardsMasterchefAddress,
      VEWaveRewardsMasterChef_ABI,
      'harvest', // PID, TOKENID
      [Number(pid), account]
    );
  }

  public async unstakeVEWaveByTokenId(provider: Web3Provider, pid: string, tokenId: string) {
    return sendTransaction(
      provider,
      '0xb60eA57c3108d40F81E621B4fc05E0587D5f9eC7',
      VEWaveEmissionDist_ABI,
      'withdrawAndDistribute', // PID, TOKENID
      [Number(pid), Number(tokenId)]
    );
  }

  public async approveNFTtoStake(provider: Web3Provider, tokenId: string) {
    return sendTransaction(
      provider,
      this.veWaveAddress,
      VEWaveAbi,
      'approve', // ADDRESS, TOKENID
      ['0xb60eA57c3108d40F81E621B4fc05E0587D5f9eC7', Number(tokenId)]
    );
  }

  public async harvestVEWaveByTokenId(provider: Web3Provider, pid: string, tokenId: string) {
    console.log('LETICIA: ' + tokenId);
    return sendTransaction(
      provider,
      '0xb60eA57c3108d40F81E621B4fc05E0587D5f9eC7',
      VEWaveEmissionDist_ABI,
      'harvestAndDistribute', // TOKENID
      [Number(pid), Number(tokenId)]
    );
  }

  public async harvestSecondTokenVEWaveByTokenId(provider: Web3Provider, poolId: string, tokenId: string) {
    return sendTransaction(
      provider,
      '0xb60eA57c3108d40F81E621B4fc05E0587D5f9eC7',
      VEWaveEmissionDist_ABI,
      'harvestAndDistributeAnotherToken',
      [Number(poolId), Number(tokenId)]
    );
  }

  public async balanceOfVEWaveReceipt(account: string): Promise<BigNumber> {
    return await call(this.service.provider, ERC20Abi, [
      '0x5D164d899c3bf681110C472bC2d870358d739Fa4',
      'balanceOf',
      [account]
    ]);
  }

  public async getNFTCountByUser(account: string): Promise<number> {
    return await call(this.service.provider, VEWaveEmissionDist_ABI, [
      this.veWaveEmissionDistAddress,
      'totalNftsByUser',
      [account]
    ]);
  }

  public async balanceOfVEWaveNft(tokenId: string): Promise<BigNumber> {
    return await call(this.service.provider, VEWaveAbi, [this.veWaveAddress, 'balanceOfNFT', [Number(tokenId)]]);
  }

  public async isVEWaveNftApproved(tokenId: string): Promise<string> {
    return await call(this.service.provider, VEWaveAbi, [
      this.veWaveAddress,
      'isApprovedOrOwner',
      ['0xb60eA57c3108d40F81E621B4fc05E0587D5f9eC7', Number(tokenId)]
    ]);
  }

  public async getNFTBalanceByUser(account: string): Promise<number> {
    return (await call(this.service.provider, VEWaveAbi, [this.veWaveAddress, 'balanceOf', [account]])) as number;
  }

  public async getNFTByUserAndIndex(account: string, index: number): Promise<number> {
    return (await call(this.service.provider, VEWaveAbi, [
      this.veWaveAddress,
      'tokenOfOwnerByIndex',
      [account, index]
    ])) as number;
  }

  public async getNFTUnlockDate(nftId: number) {
    return (await call(this.service.provider, VEWaveAbi, [this.veWaveAddress, 'locked__end', [nftId]])) as number;
  }

  public async getNFTLockAmount(nftId: number): Promise<number> {
    return ((await call(this.service.provider, VEWaveAbi, [this.veWaveAddress, 'locking', [nftId]])) as number) / 1e18;
    // const decimals = await call(this.service.provider, ERC20Abi, [
    //   '0x011C0Eb92053B64cBF6bEF9dFC9EE3005ce6c167',
    //   'decimals',
    //   []
    // ]);
    // return lockAmount.div(BigNumber.from(10).pow(decimals)).toNumber();
  }

  public async getEDNFTByUserAndIndex(account: string, index: number): Promise<number> {
    return (await call(this.service.provider, VEWaveEmissionDist_ABI, [
      this.veWaveEmissionDistAddress,
      'tokenIdsByUser',
      [account, index]
    ])) as number;
  }

  public async getPendingWaveReward(account: string, nftId: number): Promise<number> {
    return (
      ((await call(this.service.provider, VEWaveEmissionDist_ABI, [
        this.veWaveEmissionDistAddress,
        'pendingWave',
        ['0', account, nftId]
      ])) as number) / 1e18
    );
  }

  public async getPendingAnotherReward(account: string, nftId: number): Promise<number> {
    return (
      ((await call(this.service.provider, VEWaveEmissionDist_ABI, [
        this.veWaveEmissionDistAddress,
        'pendingAnotherToken',
        ['0', nftId, account]
      ])) as number) / 1e18
    );
  }

  // public async getAllStakedUserNFTsWithInfos(account: string, number: number): Promise<any> {
  //   const userNFTList: any[] = [];

  //   for (let i = 0; i < Number(number); i++) {
  //     const userNftId = await call(this.service.provider, VEWaveEmissionDist_ABI, [
  //       '0xb60eA57c3108d40F81E621B4fc05E0587D5f9eC7',
  //       'tokenIdsByUser',
  //       [account, i]
  //     ]);

  //     console.log('UserNftId____: ' + userNftId);

  //     const unlockDateOfNft = await call(this.service.provider, VEWaveAbi, [
  //       this.veWaveAddress,
  //       'locked__end',
  //       [userNftId]
  //     ]);

  //     console.log('UnlockDateOfNft: ' + unlockDateOfNft);

  //     const userNftPendingRewards = await call(this.service.provider, VEWaveEmissionDist_ABI, [
  //       this.veWaveEmissionDistAddress,
  //       'pendingWave',
  //       ['0', account, Number(userNftId)]
  //     ]).catch(e => {
  //       console.log('PUTAS E VINHO VERDE' + e);
  //     });

  //     console.log('UserNftPendingRewards: ' + userNftPendingRewards);

  //     const nftBalance = await call(this.service.provider, VEWaveAbi, [this.veWaveAddress, 'locking', [account]]);

  //     console.log('NftBalanceByTokenId: ' + nftBalance);

  //     userNFTList.push({
  //       nftId: Number(userNftId),
  //       nftBalance: Number(nftBalance),
  //       nftUnlockDate: Number(unlockDateOfNft),
  //       nftPendingRewards: Number(userNftPendingRewards || 0)
  //     });
  //   }

  //   return userNFTList;
  // }

  public async getVEWaveReceiptStakedAmount(account: string): Promise<BigNumber> {
    const userInfo = await call(this.service.provider, VEWaveRewardsMasterChef_ABI, [
      this.veWaveRewardsMasterchefAddress,
      'userInfo',
      [0, account]
    ]);
    return userInfo.amount;
  }

  public async getVeWaveEmissionsDistributorInfo() {
    const waveAddr = await call(this.service.provider, VEWaveEmissionDist_ABI, [
      this.veWaveEmissionDistAddress,
      'wave',
      []
    ]);

    const distTvl = await call(this.service.provider, ERC20Abi, [
      waveAddr,
      'balanceOf(address)',
      [this.veWaveEmissionDistAddress]
    ]);

    const wavePerBlock = await call(this.service.provider, VEWaveEmissionDist_ABI, [
      this.veWaveEmissionDistAddress,
      'wavePerBlock',
      []
    ]);

    const blocksPerDay = 86400;
    const daysInAYear = 365;

    const distApr =
      distTvl.toString() !== '0'
        ? (((Number(wavePerBlock) * blocksPerDay * daysInAYear) / Number(distTvl)) * 100).toFixed(2)
        : '0';

    const farmsInfo = {
      distTvl: (distTvl / 1e18).toFixed(2),
      distApr: distApr
    };

    return farmsInfo;
  }

  public async getAnotherEmissionsDistributorInfo() {
    const anotherInfo = await call(this.service.provider, VEWaveEmissionDist_ABI, [
      this.veWaveEmissionDistAddress,
      'poolInfoAnotherToken',
      ['0']
    ]);

    const anotherTvl = await call(this.service.provider, ERC20Abi, [
      anotherInfo.tokenReward,
      'balanceOf(address)',
      [this.veWaveEmissionDistAddress]
    ]);

    const blocksPerDay = 86400;
    const daysInAYear = 365;

    const anotherApr =
      anotherTvl.toString() !== '0'
        ? (
            ((Number(anotherInfo.anotherTokenPerBlock) * blocksPerDay * daysInAYear) / Number(anotherTvl)) *
            100
          ).toFixed(2)
        : '0';

    return {
      anotherTvl: (anotherTvl / 1e18).toFixed(2),
      anotherApr: anotherApr
    };
  }

  /* VEWAVE POOLS */

  public async getAllVeWaveReceiptFarmsInfo(account: string): Promise<any[]> {
    const farmsInfo: any = [];
    const pools = ref(VEWavePoolsList);

    for (const [i, pool] of Object.entries(pools.value)) {
      //   let userRewards;
      // if (account) {
      console.log('ACCOUNT OH PUTA: ' + account);
      const userRewards = await call(this.service.provider, VEWaveRewardsMasterChef_ABI, [
        '0xFFca379d748a8Eb5bB7d9EBd54591538a82998bD',
        'pendingREWARD',
        [account]
      ]).catch(err => {
        console.log('PUTAS E VINHO: ' + err);
      });
      console.log('userRewards: ' + userRewards);
      //}

      const poolUserStakes = account ? userRewards : '0';

      const poolTvl = await call(this.service.provider, ERC20Abi, [
        this.veWaveReceiptAddress,
        'balanceOf(address)',
        [this.veWaveRewardsMasterchefAddress]
      ]);

      console.log('pooltvl: ' + poolTvl);

      const poolRewardPerBlock = await call(this.service.provider, VEWaveRewardsMasterChef_ABI, [
        this.veWaveRewardsMasterchefAddress,
        'rewardPerBlock',
        []
      ]);

      console.log('poolRewardPerBlock: ' + poolRewardPerBlock);

      const blocksPerDay = 86400;
      const daysInAYear = 365;

      console.log('REWARD: ' + poolUserStakes);
      console.log('POOL INFOS', poolUserStakes, Number(poolTvl), poolRewardPerBlock);

      const poolApr =
        poolRewardPerBlock.toString() !== '0'
          ? (((Number(poolRewardPerBlock) * blocksPerDay * daysInAYear) / Number(poolTvl)) * 100).toFixed(2)
          : '0';

      console.log('poolApr: ' + poolApr);

      farmsInfo.push({
        poolId: i,
        poolPid: pool.poolPid,
        poolUserStakes: poolUserStakes / 1e18,
        poolTvl: Number(poolTvl) / 1e18,
        poolApr: poolApr
      });
    }

    return farmsInfo;
  }

  public async stakeVeWavePool(provider: Web3Provider, amount: string, account: string) {
    return sendTransaction(
      provider,
      '0xFFca379d748a8Eb5bB7d9EBd54591538a82998bD',
      VEWaveRewardsMasterChef_ABI,
      'deposit', // amount, to
      [BigNumber.from(amount), account]
    );
  }

  public async unstakeVeWavePool(provider: Web3Provider, pid: string, amount: string, account: string) {
    return sendTransaction(
      provider,
      this.veWaveRewardsMasterchefAddress,
      VEWaveRewardsMasterChef_ABI,
      'withdrawAndHarvest(uint256, uint256, address)', // pid, amount, to
      [Number(pid), Number(amount), account]
    );
  }

  public async harvestVeWavePool(provider: Web3Provider, pid: string, account: string) {
    return sendTransaction(
      provider,
      this.veWaveRewardsMasterchefAddress,
      VEWaveRewardsMasterChef_ABI,
      'harvest(uint256, address)', // pid, to
      [Number(pid), account]
    );
  }

  public async allowanceForVeWavePools(account: string): Promise<BigNumber> {
    return await call(this.service.provider, ERC20Abi, [
      this.veWaveReceiptAddress,
      'allowance',
      [account, this.veWaveRewardsMasterchefAddress]
    ]);
  }
}
